/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { safeReturnPath } from '@/utils/safeReturnPath';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Layout, ProtectedRoute } from '@/components/layout';
import { useAuthStore, useAppStore } from '@/store';
import { ownerSettingsApi, DEFAULT_GUI_PAGES } from '@/api/ownerSettings';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { Constants } from '@/types';

// All pages lazy-loaded — each becomes a separate webpack chunk
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ln = (imp: () => Promise<any>, name: string): React.LazyExoticComponent<any> => React.lazy(() => imp().then((m: any) => ({ default: m[name] })));
const ld = (imp: () => Promise<any>): React.LazyExoticComponent<any> => React.lazy(imp);

const LoginPage = ln(() => import('@/pages/LoginPage'), 'LoginPage');
const DashboardPage = ln(() => import('@/pages/DashboardPage'), 'DashboardPage');
const DownloadAppPage = ln(() => import('@/pages/DownloadAppPage'), 'DownloadAppPage');
const GuidesPage = ln(() => import('@/pages/GuidesPage'), 'GuidesPage');
const AllVehiclesPage = ln(() => import('@/pages/AllVehiclesPage'), 'AllVehiclesPage');
const RatesPage = ln(() => import('@/pages/RatesPage'), 'RatesPage');
const PricingSeasonsPage = ln(() => import('@/pages/PricingSeasonsPage'), 'PricingSeasonsPage');
const CarSharingPage = ln(() => import('@/pages/CarSharingPage'), 'CarSharingPage');
const CarFormPage = ln(() => import('@/pages/CarFormPage'), 'CarFormPage') as React.ComponentType<any>;
const LicensePlatesPage = ln(() => import('@/pages/LicensePlatesPage'), 'LicensePlatesPage');
const PaymentsPage = ln(() => import('@/pages/PaymentsPage'), 'PaymentsPage') as React.ComponentType<any>;
const ChargesPage = ld(() => import('@/pages/ChargesPage'));
const DriverInvoicesPage = ld(() => import('@/pages/DriverInvoicesPage'));
const BookingsPage = ln(() => import('@/pages/BookingsPage'), 'BookingsPage');
const PendingBookingsPage = ld(() => import('@/pages/PendingBookingsPage')) as React.ComponentType<any>;
const TollsPage = ld(() => import('@/pages/TollsPage'));
const GpsTrackingPage = ld(() => import('@/pages/GpsTrackingPage'));
const VehicleGpsPage = ld(() => import('@/pages/VehicleGpsPage'));
const ViolationsPage = ld(() => import('@/pages/ViolationsPage'));
const UsersPage = ln(() => import('@/pages/UsersPage'), 'UsersPage');
const StripeCardsPage = ln(() => import('@/pages/StripeCardsPage'), 'StripeCardsPage');
const OwnerInvoicesPage = ln(() => import('@/pages/OwnerInvoicesPage'), 'OwnerInvoicesPage');
const DriverCardsPage = ln(() => import('@/pages/DriverCardsPage'), 'DriverCardsPage');
const SmsLogPage = ln(() => import('@/pages/SmsLogPage'), 'SmsLogPage');
const CommercialCarsPage = ln(() => import('@/pages/CommercialCarsPage'), 'CommercialCarsPage');
const SignUpPage = ln(() => import('@/pages/SignUpPage'), 'SignUpPage');
const ResetPasswordPage = ln(() => import('@/pages/ResetPasswordPage'), 'ResetPasswordPage');
const StripeCallbackPage = ln(() => import('@/pages/StripeCallbackPage'), 'StripeCallbackPage');
const RegistrationVerifyPage = ln(() => import('@/pages/RegistrationVerifyPage'), 'RegistrationVerifyPage');
const RentalAgreementPage = ln(() => import('@/pages/RentalAgreementPage'), 'RentalAgreementPage');
const TermsConditionsPage = ln(() => import('@/pages/TermsConditionsPage'), 'TermsConditionsPage');
const PrivacyPolicyPage = ln(() => import('@/pages/PrivacyPolicyPage'), 'PrivacyPolicyPage');
const DeleteAccountPage = ln(() => import('@/pages/DeleteAccountPage'), 'DeleteAccountPage');
const BecomeOwnerPage = ln(() => import('@/pages/BecomeOwnerPage'), 'BecomeOwnerPage');
const PartnersPage = ln(() => import('@/pages/PartnersPage'), 'PartnersPage');
const PartnerOwnerSettingsPage = ln(() => import('@/pages/PartnerOwnerSettingsPage'), 'PartnerOwnerSettingsPage');
const RentalTermsPage = ln(() => import('@/pages/RentalTermsPage'), 'RentalTermsPage');
const OwnersPage = ln(() => import('@/pages/OwnersPage'), 'OwnersPage');
const OwnerTollSettingsPage = ld(() => import('@/pages/OwnerTollSettingsPage'));
const CreateDriverPage = ld(() => import('@/pages/CreateDriverPage'));
const CreateOwnerPage = ld(() => import('@/pages/CreateOwnerPage'));
const InvitationPage = ld(() => import('@/pages/InvitationPage'));
const IntroPage = ld(() => import('@/pages/IntroPage'));
const CreatePartnerPage = ld(() => import('@/pages/CreatePartnerPage'));
const PartnerLandingPage = ld(() => import('@/pages/PartnerLandingPage'));
const PreRegistrationPage = ld(() => import('@/pages/PreRegistrationPage'));
const CompanySettingsPage = ln(() => import('@/pages/CompanySettingsPage'), 'CompanySettingsPage');
const AdminSettingsPage = ln(() => import('@/pages/AdminSettingsPage'), 'AdminSettingsPage');
const DesignPage = ln(() => import('@/pages/DesignPage'), 'DesignPage');
const LocationsPage = ln(() => import('@/pages/LocationsPage'), 'LocationsPage');
const CommonLocationsAdminPage = ln(() => import('@/pages/CommonLocationsAdminPage'), 'CommonLocationsAdminPage');
const PartnerIntegrationsAdminPage = ln(() => import('@/pages/PartnerIntegrationsAdminPage'), 'PartnerIntegrationsAdminPage');
const DataChangeLogPage = ln(() => import('@/pages/DataChangeLogPage'), 'DataChangeLogPage');
const MessagesPage = ln(() => import('@/pages/MessagesPage'), 'MessagesPage');
const DisputesPage = ln(() => import('@/pages/DisputesPage'), 'DisputesPage');
const CollectionsQueuePage = ln(() => import('@/pages/CollectionsQueuePage'), 'CollectionsQueuePage');
const DisputeDetailPage = ln(() => import('@/pages/DisputeDetailPage'), 'DisputeDetailPage');
const StripeDashboardPage = ln(() => import('@/pages/StripeDashboardPage'), 'StripeDashboardPage');
const PlatformStripeDashboardPage = ln(() => import('@/pages/PlatformStripeDashboardPage'), 'PlatformStripeDashboardPage');
const AccountStatementReportPage = ln(() => import('@/pages/AccountStatementReportPage'), 'AccountStatementReportPage');
const AnnualReportPage = ln(() => import('@/pages/AnnualReportPage'), 'AnnualReportPage');
const PlatformAccountStatementReportPage = ln(() => import('@/pages/PlatformAccountStatementReportPage'), 'PlatformAccountStatementReportPage');
const PlatformAnnualReportPage = ln(() => import('@/pages/PlatformAnnualReportPage'), 'PlatformAnnualReportPage');
const CreateUserAndAgreementPage = ln(() => import('@/pages/create-user-and-agreement/CreateUserAndAgreementPage'), 'CreateUserAndAgreementPage');
const OwnerAgreementSignPage = ln(() => import('@/pages/OwnerAgreementSignPage'), 'OwnerAgreementSignPage');
const LinkExpiredPage = ln(() => import('@/pages/LinkExpiredPage'), 'LinkExpiredPage');
const ConnectGpsProviderPage = ln(() => import('@/pages/ConnectGpsProviderPage'), 'ConnectGpsProviderPage');

/** A signed-in visitor on /login goes where ProtectedRoute meant to send them, not always to "/". */
const SignedInLoginRedirect: React.FC = () => {
  const location = useLocation();
  const target = safeReturnPath((location.state as { from?: unknown } | null)?.from) ?? '/';
  return <Navigate to={target} replace />;
};
const AddCardPage = ln(() => import('@/pages/AddCardPage'), 'AddCardPage');
const RenterTollsPage = ld(() => import('@/pages/RenterTollsPage'));
const TollProviderPage = ln(() => import('@/pages/TollProviderPage'), 'TollProviderPage');
const TollTransactionsPage = ln(() => import('@/pages/TollTransactionsPage'), 'TollTransactionsPage');
const OwnerViolationsPage = ln(() => import('@/pages/OwnerViolationsPage'), 'OwnerViolationsPage');
const SuperchargerPage = ln(() => import('@/pages/SuperchargerPage'), 'SuperchargerPage');
const PluginsPage = ln(() => import('@/pages/PluginsPage'), 'PluginsPage');
const PluginsSettingsPage = ln(() => import('@/pages/PluginsSettingsPage'), 'PluginsSettingsPage');
const ScrapeWorkerPage = ln(() => import('@/pages/ScrapeWorkerPage'), 'ScrapeWorkerPage');
const PlatformInvoicesPage = ln(() => import('@/pages/PlatformInvoicesPage'), 'PlatformInvoicesPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Minimal loading fallback for lazy-loaded pages (no heavy dependencies)
const LazyFallback: React.FC = () => {
  const { t } = useTranslation();
  return (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-gray-500 text-sm">{t('common.loading', 'Loading...')}</p>
    </div>
  </div>
  );
};

// Catches lazy-chunk load failures that happen when a new build was deployed
// while the tab was still open (the old chunk hashes no longer exist on the
// server). A one-time hard reload pulls the fresh index.html with the new chunk
// manifest; the sessionStorage guard prevents an infinite reload loop, and it is
// cleared on the next successful render so a later deploy can reload again.
class ChunkErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(error: any) {
    const msg = String(error?.message || error || '');
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk/i.test(msg) ||
      /dynamically imported module/i.test(msg);
    if (isChunkError && !sessionStorage.getItem('chunk_reloaded')) {
      sessionStorage.setItem('chunk_reloaded', '1');
      window.location.reload();
    }
    return { failed: isChunkError };
  }

  componentDidUpdate() {
    // Clear the guard once a render succeeds so future deploys can reload again
    if (!this.state.failed) {
      sessionStorage.removeItem('chunk_reloaded');
    }
  }

  render() {
    // Show the spinner briefly while the reload kicks in
    if (this.state.failed) {
      return <LazyFallback />;
    }
    return this.props.children;
  }
}

const WelcomePage: React.FC = () => {
  const { t } = useTranslation();
  return (
  <div className="flex items-center justify-center p-8" style={{ minHeight: 'calc(100vh - 160px)' }}>
    <div className="text-center">
      <img src="/images/logo_with_text.png" alt="Aegis" className="w-52 mx-auto mb-8" />
      <p className="text-gray-600 text-lg">{t('welcome.title', 'Welcome to My E-Z Toll')}</p>
    </div>
  </div>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, isOwner, isAdmin, user } = useAuthStore();
  const { setGuiSettings } = useAppStore();
  useInactivityTimeout();

  // Load GUI settings for the logged-in user on login/reload
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }
    ownerSettingsApi.getSettings(user.id).then((settings) => {
      if (settings.gui?.pages) {
        setGuiSettings(settings.gui);
      } else {
        // No GUI config saved yet — use defaults (all visible)
        setGuiSettings({ pages: { ...DEFAULT_GUI_PAGES } });
      }
    });
  }, [isAuthenticated, user?.id, setGuiSettings]);

  return (
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <Toaster
        position="top-center"
        containerStyle={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed',
          zIndex: 99999,
        }}
        toastOptions={{
          style: {
            background: '#1f2937',
            color: '#f9fafb',
            borderRadius: '12px',
            padding: '16px 24px',
            fontSize: '15px',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          },
          success: {
            duration: 4000,
            style: {
              border: '1px solid #22c55e',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 20px rgba(34, 197, 94, 0.15)',
            },
            iconTheme: {
              primary: '#22c55e',
              secondary: '#f9fafb',
            },
          },
          error: {
            duration: 5000,
            style: {
              border: '1px solid #ef4444',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 20px rgba(239, 68, 68, 0.15)',
            },
            iconTheme: {
              primary: '#ef4444',
              secondary: '#f9fafb',
            },
          },
        }}
      />
      <ChunkErrorBoundary>
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <SignedInLoginRedirect /> : <LoginPage />
          }
        />

        <Route
          path="/signup"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <SignUpPage />
          }
        />

        <Route
          path="/reset-password"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <ResetPasswordPage />
          }
        />

        {/* Stripe callback - public route for popup window */}
        <Route path="/stripe-callback" element={<StripeCallbackPage />} />
        {/* StripeView/* — Stripe redirects here after checkout; handle client-side
            as fallback when Node BFF proxy to .NET API is unavailable */}
        <Route path="/StripeView/success" element={<StripeCallbackPage />} />
        <Route path="/StripeView/success-by-link" element={<StripeCallbackPage />} />
        <Route path="/StripeView/fail" element={<StripeCallbackPage />} />

        {/* Registration verification - public route */}
        <Route path="/UserAuth/:userId/registration-verify" element={<RegistrationVerifyPage />} />

        {/* Rental agreement - public route (lazy-loaded) */}
        <Route path="/rental-agreement/:requestId" element={<RentalAgreementPage />} />

        {/* Driver registration + agreement - public route (lazy-loaded) */}
        <Route path="/create-user-and-agreement/:authKey" element={<CreateUserAndAgreementPage />} />
        <Route path="/owner-agreement/:authKey" element={<OwnerAgreementSignPage />} />

        {/* Add credit card - public route (lazy-loaded) */}
        <Route path="/add-card/:authKey" element={<AddCardPage />} />
        <Route path="/link-expired" element={<LinkExpiredPage />} />

        {/* One-click GPS connect from a link — Samsara's Direct Install URL lands here. Behind
            ProtectedRoute: a signed-out visitor signs in and the login page returns them here. */}
        <Route
          path="/connect/:providerId"
          element={
            <ProtectedRoute requireOwner>
              <ConnectGpsProviderPage />
            </ProtectedRoute>
          }
        />

        {/* Terms & Conditions - public route with optional owner ID */}
        <Route path="/terms" element={<TermsConditionsPage />} />
        <Route path="/terms/:ownerId" element={<TermsConditionsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy/:ownerId" element={<PrivacyPolicyPage />} />

        {/* Account deletion request — public. Google Play requires this URL to be reachable
            without installing the app; it is registered in the Play listing. */}
        <Route path="/delete-account" element={<DeleteAccountPage />} />

        {/* Rental Terms - public route with optional owner ID */}
        <Route path="/rental-terms" element={<RentalTermsPage />} />
        <Route path="/rental-terms/:ownerId" element={<RentalTermsPage />} />

        {/* Pre-registration: create user + agreement + card (no booking, lazy-loaded) */}
        <Route path="/create-user/:ownerId" element={<PreRegistrationPage />} />

        {/* Create User without ownerId — redirect or show PreRegistrationPage */}
        <Route path="/create-user" element={<CreateDriverPage />} />
        {/* Owner access for an account that already exists. Registration refuses an address it
            already knows, so without this a renter buying a car to rent out has to invent a second
            email address. Public: the page itself only explains until the visitor has signed in. */}
        <Route path="/become-owner" element={<BecomeOwnerPage />} />

        <Route path="/create-owner" element={
          isAuthenticated
            ? <Layout><CreateOwnerPage /></Layout>
            : <CreateOwnerPage />
        } />
        <Route path="/invitation" element={<InvitationPage />} />
        <Route path="/intro" element={<IntroPage />} />
        <Route path="/create-partner" element={
          isAuthenticated
            ? <Layout><CreatePartnerPage /></Layout>
            : <CreatePartnerPage />
        } />
        <Route path="/partner" element={<PartnerLandingPage />} />

        {/* Guides (PDF) — public, so the login page can link to them; wears the app chrome once signed in */}
        <Route path="/guides" element={
          isAuthenticated
            ? <Layout><GuidesPage /></Layout>
            : <GuidesPage />
        } />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              {isOwner || isAdmin ? (
                <Layout>
                  <DashboardPage />
                </Layout>
              ) : (
                <Layout>
                  <WelcomePage />
                </Layout>
              )}
            </ProtectedRoute>
          }
        />

        {/* Mobile app download page (standard header + footer) */}
        <Route
          path="/download"
          element={
            <ProtectedRoute>
              <Layout>
                <DownloadAppPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* All Vehicles routes */}
        <Route
          path="/all-vehicles"
          element={
            <ProtectedRoute requireOwner guiKey="allVehicles">
              <Layout>
                <AllVehiclesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/all-vehicles/new"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/all-vehicles/:id/gps"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <VehicleGpsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/all-vehicles/:id"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Rates (per-category / make / model / car pricing tree) */}
        <Route
          path="/rates"
          element={
            <ProtectedRoute requireOwner guiKey="rates">
              <Layout>
                <RatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Pricing seasons (date-range rates + per-car season prices) */}
        <Route
          path="/pricing-seasons"
          element={
            <ProtectedRoute requireOwner guiKey="pricingSeasons">
              <Layout>
                <PricingSeasonsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Car Sharing routes */}
        <Route
          path="/car-sharing"
          element={
            <ProtectedRoute requireOwner guiKey="carSharing">
              <Layout>
                <CarSharingPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/car-sharing/new"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.CarSharingId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/car-sharing/:id"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.CarSharingId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Commercial Cars routes */}
        <Route
          path="/commercial-cars"
          element={
            <ProtectedRoute requireOwner guiKey="commercialCars">
              <Layout>
                <CommercialCarsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/commercial-cars/new"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.CommercialCarId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/commercial-cars/:id"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.CommercialCarId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* License Plates routes */}
        <Route
          path="/license-plates"
          element={
            <ProtectedRoute requireOwner guiKey="licensePlates">
              <Layout>
                <LicensePlatesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/license-plates/new"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.LicensePlateId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/license-plates/:id"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <CarFormPage carTypeFilter={Constants.LicensePlateId} />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Payment routes */}
        <Route
          path="/payments/charges"
          element={
            <ProtectedRoute requireOwner guiKey="charges">
              <Layout>
                <ChargesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* One driver's invoice lines. Reached from the drivers menu, never the sidebar, so it rides on
            the Charges switch: it bills the very same charges, and closing Charges must not leave a back
            door to them. */}
        <Route
          path="/payments/invoices"
          element={
            <ProtectedRoute requireOwner guiKey="charges">
              <Layout>
                <DriverInvoicesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bookings"
          element={
            <ProtectedRoute requireOwner guiKey="bookings">
              <Layout>
                <BookingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/p2p-bookings/:platformId"
          element={
            <ProtectedRoute requireOwner guiKey="bookings">
              <Layout>
                <PendingBookingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payments/tolls"
          element={
            <ProtectedRoute guiKey="tolls">
              <Layout>
                <TollsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gps-tracking"
          element={
            <ProtectedRoute requireOwner guiKey="gpsTracking">
              <Layout>
                <GpsTrackingPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payments/violations"
          element={
            <ProtectedRoute guiKey="violations">
              <Layout>
                <ViolationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/toll-provider/:accountId"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <TollProviderPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/toll-transactions"
          element={
            <ProtectedRoute requireOwner guiKey="tollTransactions">
              <Layout>
                <TollTransactionsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/violations"
          element={
            <ProtectedRoute requireOwner guiKey="violations">
              <Layout>
                <OwnerViolationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/supercharger"
          element={
            <ProtectedRoute requireOwner guiKey="supercharger">
              <Layout>
                <SuperchargerPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payments/failed"
          element={
            <ProtectedRoute requireOwner guiKey="failedPayments">
              <Layout>
                <PaymentsPage type="failed" />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/payments/successful"
          element={
            <ProtectedRoute requireOwner guiKey="successfulPayments">
              <Layout>
                <PaymentsPage type="successful" />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Users route */}
        <Route
          path="/users"
          element={
            <ProtectedRoute requireOwner guiKey="users">
              <Layout>
                <UsersPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Owners route (admin sees list, owner redirects to own settings) */}
        <Route
          path="/owners"
          element={
            <ProtectedRoute requireOwner guiKey="tollDistributions">
              <Layout>
                <OwnersPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Partners route (admin or partner — lists owners where PartnerId = current/selected user) */}
        <Route
          path="/partners"
          element={
            <ProtectedRoute requireOwner guiKey="partners">
              <Layout>
                <PartnersPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Partners → row menu: Owner Settings / Booking Plugins / Page Visibility of one owner.
            Same access as Administration → Settings. */}
        <Route
          path="/partners/:ownerId/:section"
          element={
            <ProtectedRoute requireAdmin={true} allowPartner={true}>
              <Layout>
                <PartnerOwnerSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Owner Toll Settings route (admin + owner self-service) */}
        <Route
          path="/owners/:ownerId"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <OwnerTollSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Stripe Cards route */}
        <Route
          path="/stripe-cards"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <StripeCardsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Owner billing — invoices to pay */}
        <Route
          path="/billing/invoices"
          element={
            <ProtectedRoute requireOwner guiKey="invoicesToPay">
              <Layout>
                <OwnerInvoicesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Driver Cards route */}
        <Route
          path="/driver-cards"
          element={
            <ProtectedRoute>
              <Layout>
                <DriverCardsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Company → Locations route - Admin and Owners */}
        <Route
          path="/locations"
          element={
            <ProtectedRoute requireOwner={true} guiKey="locations">
              <Layout>
                <LocationsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Administration → Shared Locations (canonical, cross-owner) - Admin */}
        <Route
          path="/admin/locations"
          element={
            <ProtectedRoute requireOwner={true}>
              <Layout>
                <CommonLocationsAdminPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Administration → Integrations - Admin only: booking platforms on the Partner API (tokens, partner
            account, owner defaults, billing). Tokens and payer accounts are never shown to anyone else. */}
        <Route
          path="/admin/integrations"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PartnerIntegrationsAdminPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Company Settings route (owner-facing tabs/fields) - Admin, Owners, and Partners (for their owners) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute requireOwner={true} allowPartner={true} guiKey="settings">
              <Layout>
                <CompanySettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Administration → Change History - Admin only. The screen names every operator, their IP
            and the values they touched across all owners, so it never widens past requireAdmin. */}
        <Route
          path="/admin/change-log"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <DataChangeLogPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Administration → Settings - Admin, or Partner (for their owners' settings) */}
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireAdmin={true} allowPartner={true}>
              <Layout>
                <AdminSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Storefront Design route - Admin and Owners */}
        <Route
          path="/design"
          element={
            <ProtectedRoute requireOwner={true} guiKey="design">
              <Layout>
                <DesignPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Messages route — owner email relay compose + history */}
        <Route
          path="/messages"
          element={
            <ProtectedRoute requireOwner={true} guiKey="messages">
              <Layout>
                <MessagesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* SMS & QR Log route - available to all authenticated users */}
        <Route
          path="/sms-log"
          element={
            <ProtectedRoute guiKey="smsLog">
              <Layout>
                <SmsLogPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Renter Tolls - available to all authenticated users */}
        <Route
          path="/renter-tolls"
          element={
            <ProtectedRoute>
              <Layout>
                <RenterTollsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Stripe Dashboard */}
        <Route
          path="/payments/stripe-dashboard"
          element={
            <ProtectedRoute requireOwner guiKey="stripeDashboard">
              <Layout>
                <StripeDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Collections: the approval queue for referring an uncollected debt to an agency. */}
        <Route
          path="/payments/collections"
          element={
            <ProtectedRoute requireOwner guiKey="collections">
              <Layout>
                <CollectionsQueuePage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Disputes routes */}
        <Route
          path="/disputes"
          element={
            <ProtectedRoute requireOwner guiKey="disputes">
              <Layout>
                <DisputesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/disputes/:id"
          element={
            <ProtectedRoute requireOwner>
              <Layout>
                <DisputeDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Reports routes */}
        <Route
          path="/reports/account-statement"
          element={
            <ProtectedRoute requireOwner guiKey="accountStatementReport">
              <Layout>
                <AccountStatementReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/annual"
          element={
            <ProtectedRoute requireOwner guiKey="annualReport">
              <Layout>
                <AnnualReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Platform Reports - Admin only */}
        <Route
          path="/admin/reports/account-statement"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PlatformAccountStatementReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/reports/annual"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PlatformAnnualReportPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Plugin management - Admin only (hot-reload toll plugin DLLs) */}
        <Route
          path="/admin/plugins"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PluginsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Plugin settings - Admin only (per-plugin config: toll / violation / booking) */}
        <Route
          path="/admin/plugins-settings"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PluginsSettingsPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Scrape Worker - Admin only */}
        <Route
          path="/admin/scrape-worker"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <ScrapeWorkerPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Platform Stripe Dashboard - Admin only */}
        <Route
          path="/admin/platform-stripe-dashboard"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PlatformStripeDashboardPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Platform Invoices - Admin only */}
        <Route
          path="/admin/platform-invoices"
          element={
            <ProtectedRoute requireAdmin={true}>
              <Layout>
                <PlatformInvoicesPage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
    </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
